import React from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { MEDIA } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { SlidersHorizontal, X, ChevronRight, ArrowRight } from "lucide-react";

/* ── Skeleton card ───────────────────────────── */
function ProductSkeleton() {
    return (
        <div className="flex flex-col bg-card rounded-2xl overflow-hidden border border-border/60">
            <div className="skeleton aspect-[4/3] w-full" />
            <div className="p-3 sm:p-4 space-y-2.5">
                <div className="skeleton h-2.5 w-1/3 rounded-full" />
                <div className="skeleton h-3.5 w-full rounded-full" />
                <div className="skeleton h-3.5 w-3/4 rounded-full" />
                <div className="flex items-center justify-between pt-2 border-t border-border/40">
                    <div className="skeleton h-6 w-1/3 rounded-full" />
                    <div className="skeleton h-8 w-1/3 rounded-xl" />
                </div>
            </div>
        </div>
    );
}

/* ── Category card used on landing ───────────── */
function CategoryCard({ cat, index }) {
    return (
        <Link
            to={`/magazin/${cat.slug}`}
            className="group relative bg-card rounded-2xl overflow-hidden border border-border/60 hover:border-primary/40 hover:shadow-xl transition-all duration-300 flex flex-col"
            style={{ animationDelay: `${Math.min(index, 8) * 0.06}s` }}
            data-testid={`landing-cat-${cat.slug}`}
        >
            {/* Image / Placeholder */}
            {cat.image ? (
                <div className="aspect-[16/9] overflow-hidden bg-muted">
                    <img
                        src={cat.image.startsWith("http") ? cat.image : MEDIA(cat.image)}
                        alt={cat.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                </div>
            ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-primary/8 via-muted to-muted/60 flex items-center justify-center">
                    <span className="text-5xl font-heading font-bold text-primary/15 select-none">
                        {cat.name.charAt(0).toUpperCase()}
                    </span>
                </div>
            )}
            {/* Info */}
            <div className="flex items-center justify-between p-4 gap-2">
                <div>
                    <h3 className="font-heading font-semibold text-base leading-snug">{cat.name}</h3>
                    {cat.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{cat.description}</p>
                    )}
                </div>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <ArrowRight className="h-3.5 w-3.5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
            </div>
        </Link>
    );
}

/* ── Shop landing (no slug, no query) ─────────── */
function ShopLanding({ topCategories, categories }) {
    const [featured, setFeatured] = React.useState([]);
    const [newest, setNewest] = React.useState([]);
    const [loadingFeatured, setLoadingFeatured] = React.useState(true);

    React.useEffect(() => {
        api.get("/products", { params: { featured: true, limit: 8, sort: "newest" } })
            .then(r => setFeatured(r.data.items || []))
            .catch(() => {})
            .finally(() => setLoadingFeatured(false));
        api.get("/products", { params: { limit: 8, sort: "newest" } })
            .then(r => setNewest(r.data.items || []))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="shop-landing">
            {/* Heading */}
            <div className="mb-10">
                <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl uppercase tracking-tighter font-bold">Magazin</h1>
                <p className="text-muted-foreground mt-2 text-sm sm:text-base">Alege o categorie sau caută direct produsul dorit.</p>
            </div>

            {/* Categories grid */}
            {topCategories.length > 0 && (
                <section className="mb-14">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-heading text-xl font-semibold">Categorii</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-fade-up">
                        {topCategories.map((cat, i) => (
                            <CategoryCard key={cat.category_id} cat={cat} index={i} />
                        ))}
                    </div>
                    {/* Subcategory chips under each top category */}
                    {topCategories.map(cat => {
                        const subs = categories.filter(c => c.parent_id === cat.category_id);
                        if (!subs.length) return null;
                        return (
                            <div key={cat.category_id} className="mt-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <Link to={`/magazin/${cat.slug}`} className="text-sm font-semibold hover:text-primary transition-colors">{cat.name}</Link>
                                    <span className="text-muted-foreground text-xs">→</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {subs.map(sc => (
                                        <Link
                                            key={sc.category_id}
                                            to={`/magazin/${sc.slug}`}
                                            className="px-3 py-1.5 rounded-full text-xs font-medium border border-border hover:border-primary hover:text-primary transition-colors bg-card"
                                            data-testid={`landing-subcat-${sc.slug}`}
                                        >
                                            {sc.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            {/* Featured / Recomandate */}
            {(loadingFeatured || featured.length > 0) && (
                <section className="mb-14">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-heading text-xl font-semibold">Recomandate</h2>
                        <Link to="/magazin?sort=featured" className="text-sm text-primary hover:underline flex items-center gap-1">
                            Vezi toate <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    {loadingFeatured ? (
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                            {Array.from({ length: 4 }).map((_, i) => <ProductSkeleton key={i} />)}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                            {featured.map((p, i) => (
                                <ProductCard key={p.product_id} product={p} style={{ animationDelay: `${i * 0.05}s` }} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Nou în stoc */}
            {newest.length > 0 && (
                <section className="mb-14">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-heading text-xl font-semibold">Nou în stoc</h2>
                        <Link to="/magazin?sort=newest" className="text-sm text-primary hover:underline flex items-center gap-1">
                            Vezi toate <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                        {newest.map((p, i) => (
                            <ProductCard key={p.product_id} product={p} style={{ animationDelay: `${i * 0.05}s` }} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

/* ── Main Shop component ──────────────────────── */
export default function Shop() {
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const q = searchParams.get("q") || "";
    const [category, setCategory] = React.useState(null);
    const [categories, setCategories] = React.useState([]);
    const [products, setProducts] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [filters, setFilters] = React.useState({ brand: "", min_price: "", max_price: "", spec_key: "", spec_value: "", sort: "newest" });
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const isLanding = !slug && !q;

    React.useEffect(() => {
        api.get("/categories").then(r => setCategories(r.data || []));
    }, []);

    React.useEffect(() => {
        if (slug) {
            api.get(`/categories/${slug}`).then(r => setCategory(r.data)).catch(() => setCategory(null));
        } else {
            setCategory(null);
        }
    }, [slug]);

    React.useEffect(() => {
        if (isLanding) return; // landing handles its own data
        setLoading(true);
        const params = { limit: 60, sort: filters.sort };
        if (slug) params.category = slug;
        if (q) params.q = q;
        if (filters.brand) params.brand = filters.brand;
        if (filters.min_price) params.min_price = filters.min_price;
        if (filters.max_price) params.max_price = filters.max_price;
        if (filters.spec_key && filters.spec_value) {
            params.spec_key = filters.spec_key;
            params.spec_value = filters.spec_value;
        }
        api.get("/products", { params }).then(r => {
            setProducts(r.data.items || []);
            setTotal(r.data.total || 0);
        }).finally(() => setLoading(false));
    }, [slug, q, filters, isLanding]);

    /* ── Derived hierarchy ── */
    const topCategories = categories.filter(c => !c.parent_id);
    const isSubcategory = category && !!category.parent_id;
    const parentCatId = category ? (category.parent_id || category.category_id) : null;
    const subCategories = parentCatId ? categories.filter(c => c.parent_id === parentCatId) : [];
    const parentCategory = parentCatId ? categories.find(c => c.category_id === parentCatId) : null;

    const brands = [...new Set(products.map(p => p.brand).filter(Boolean))];

    const updateFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
    const resetFilters = () => {
        setFilters({ brand: "", min_price: "", max_price: "", spec_key: "", spec_value: "", sort: "newest" });
        setSearchParams({});
    };

    /* ── Landing page ── */
    if (isLanding) {
        return (
            <div className="container-x py-8 sm:py-12" data-testid="shop-page">
                <ShopLanding topCategories={topCategories} categories={categories} />
            </div>
        );
    }

    /* ── Category / search product grid ── */
    return (
        <div className="container-x py-8 sm:py-12" data-testid="shop-page">

            {/* ── Page heading ── */}
            <div className="mb-8">
                {/* Breadcrumb */}
                {(category || q) && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 font-mono">
                        <Link to="/magazin" className="hover:text-primary transition-colors">Magazin</Link>
                        {isSubcategory && parentCategory && (
                            <>
                                <ChevronRight className="h-3 w-3" />
                                <Link to={`/magazin/${parentCategory.slug}`} className="hover:text-primary transition-colors">{parentCategory.name}</Link>
                            </>
                        )}
                        {category && (
                            <>
                                <ChevronRight className="h-3 w-3" />
                                <span className="text-foreground">{category.name}</span>
                            </>
                        )}
                        {q && (
                            <>
                                <ChevronRight className="h-3 w-3" />
                                <span className="text-foreground">Căutare</span>
                            </>
                        )}
                    </div>
                )}

                <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl uppercase tracking-tighter font-bold">
                    {category ? category.name : q ? `Rezultate: "${q}"` : "Toate produsele"}
                </h1>
                {category?.description && (
                    <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">{category.description}</p>
                )}
                <div className="font-mono text-xs text-muted-foreground mt-2">{total} produse</div>

                {/* ── Mobile-only subcategory strip ── */}
                {subCategories.length > 0 && (
                    <div className="lg:hidden mt-4 -mx-5 px-5 overflow-x-auto no-scrollbar">
                        <div className="flex gap-2 pb-1 w-max">
                            <Link
                                to={`/magazin/${parentCategory?.slug}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${!isSubcategory ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                            >
                                Toate
                            </Link>
                            {subCategories.map(sc => (
                                <Link
                                    key={sc.category_id}
                                    to={`/magazin/${sc.slug}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${slug === sc.slug ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                                    data-testid={`subcat-chip-${sc.slug}`}
                                >
                                    {sc.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* ── Sidebar ── */}
                <aside className="lg:col-span-3 space-y-6">
                    <button
                        className="lg:hidden btn-outline w-full flex items-center justify-center gap-2"
                        onClick={() => setFiltersOpen(true)}
                        data-testid="mobile-filter-toggle"
                    >
                        <SlidersHorizontal className="h-4 w-4" /> Filtre & Categorii
                    </button>

                    <div className={`lg:block ${filtersOpen ? "fixed inset-0 z-[200] bg-background overflow-y-auto" : "hidden"}`}>
                        {filtersOpen && (
                            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-background z-10">
                                <span className="font-heading text-lg">Filtre & Categorii</span>
                                <button className="btn-ghost" onClick={() => setFiltersOpen(false)}><X className="h-6 w-6" /></button>
                            </div>
                        )}
                        <div className="p-5 lg:p-0">
                        <div className="space-y-7">

                            {/* Categories */}
                            <div>
                                <h3 className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">Categorii</h3>
                                <div className="space-y-0.5">
                                    <Link
                                        to="/magazin"
                                        className="flex items-center gap-2 py-2 px-3 text-sm rounded-lg transition-colors hover:bg-muted text-foreground"
                                        data-testid="filter-all-cats"
                                    >
                                        Toate produsele
                                    </Link>
                                    {topCategories.map(c => {
                                        const isActive = slug === c.slug || (isSubcategory && category.parent_id === c.category_id);
                                        const children = categories.filter(x => x.parent_id === c.category_id);
                                        return (
                                            <div key={c.category_id}>
                                                <Link
                                                    to={`/magazin/${c.slug}`}
                                                    className={`flex items-center justify-between py-2 px-3 text-sm rounded-lg transition-colors ${isActive ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted text-foreground"}`}
                                                    data-testid={`filter-cat-${c.slug}`}
                                                >
                                                    <span>{c.name}</span>
                                                    {children.length > 0 && <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isActive ? "rotate-90 text-primary" : "text-muted-foreground"}`} />}
                                                </Link>
                                                {isActive && children.length > 0 && (
                                                    <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-primary/20 pl-3">
                                                        {children.map(sc => (
                                                            <Link
                                                                key={sc.category_id}
                                                                to={`/magazin/${sc.slug}`}
                                                                className={`block py-1.5 px-2 text-xs rounded-lg transition-colors ${slug === sc.slug ? "text-primary font-semibold bg-primary/5" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                                                                data-testid={`filter-subcat-${sc.slug}`}
                                                            >
                                                                {sc.name}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Brand filter */}
                            {brands.length > 0 && (
                                <div>
                                    <h3 className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">Brand</h3>
                                    <div className="space-y-0.5">
                                        <button
                                            onClick={() => updateFilter("brand", "")}
                                            className={`block w-full text-left py-2 px-3 text-sm rounded-lg transition-colors ${!filters.brand ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}
                                        >
                                            Toate
                                        </button>
                                        {brands.map(b => (
                                            <button
                                                key={b}
                                                onClick={() => updateFilter("brand", b)}
                                                className={`block w-full text-left py-2 px-3 text-sm rounded-lg transition-colors ${filters.brand === b ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}
                                            >
                                                {b}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Price filter */}
                            <div>
                                <h3 className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">Preț (RON)</h3>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={filters.min_price}
                                        onChange={(e) => updateFilter("min_price", e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                        data-testid="filter-min-price"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={filters.max_price}
                                        onChange={(e) => updateFilter("max_price", e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                        data-testid="filter-max-price"
                                    />
                                </div>
                            </div>

                            {/* Spec filters */}
                            {category?.specs_schema?.filter(s => s.type === "select" && s.options?.length).map(s => (
                                <div key={s.key}>
                                    <h3 className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">{s.label}</h3>
                                    <div className="space-y-0.5">
                                        <button
                                            onClick={() => setFilters(f => ({ ...f, spec_key: "", spec_value: "" }))}
                                            className={`block w-full text-left py-2 px-3 text-sm rounded-lg transition-colors ${!filters.spec_value ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}
                                        >
                                            Toate
                                        </button>
                                        {s.options.map(opt => (
                                            <button
                                                key={opt}
                                                onClick={() => setFilters(f => ({ ...f, spec_key: s.key, spec_value: opt }))}
                                                className={`block w-full text-left py-2 px-3 text-sm rounded-lg transition-colors ${filters.spec_value === opt ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <button onClick={resetFilters} className="btn-outline w-full text-sm" data-testid="reset-filters">
                                Resetează filtrele
                            </button>
                        </div>
                        </div>
                    </div>
                </aside>

                {/* ── Product grid ── */}
                <div className="lg:col-span-9">
                    {/* Sort bar */}
                    <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
                        <span className="font-mono text-xs text-muted-foreground">{total} rezultate</span>
                        <select
                            value={filters.sort}
                            onChange={(e) => updateFilter("sort", e.target.value)}
                            className="border border-border bg-card px-3 py-2 rounded-lg font-mono text-xs focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                            data-testid="sort-select"
                        >
                            <option value="newest">Cele mai noi</option>
                            <option value="price_asc">Preț crescător</option>
                            <option value="price_desc">Preț descrescător</option>
                            <option value="name">Nume A-Z</option>
                        </select>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                            {Array.from({ length: 6 }).map((_, i) => <ProductSkeleton key={i} />)}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="py-20 text-center">
                            <div className="text-4xl mb-4">🔍</div>
                            <p className="font-heading text-xl text-muted-foreground">Nu am găsit produse.</p>
                            <p className="text-sm text-muted-foreground mt-2">Încearcă să modifici filtrele sau caută altceva.</p>
                            <button onClick={resetFilters} className="btn-outline mt-6 text-sm">Resetează filtrele</button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                            {products.map((p, i) => (
                                <ProductCard
                                    key={p.product_id}
                                    product={p}
                                    style={{ animationDelay: `${Math.min(i, 9) * 0.05}s` }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
